// ─────────────────────────────────────────────────────────────────────────────
//  EditController
//
//  Encapsulates all cell-edit and row-edit state and logic for uwc-datatable.
//
//  Responsibilities:
//    • Track which cell is currently being edited (cell mode).
//    • Track which rows are currently in edit mode (row mode).
//    • Store original row/cell values so cancel can restore them.
//    • Fire host-level edit lifecycle events.
//    • Expose a sync method to seed editing state from the host's
//      `editingRowKeys` property.
//
//  Usage inside UwcDatatable:
//
//    private _edit = new EditController(this, (row) => this._getRowKey(row));
//
//    // In willUpdate:
//    if (changed.has('editMode'))       this._edit.editMode       = this.editMode;
//    if (changed.has('editingRowKeys')) this._edit.syncRowKeys(this.editingRowKeys);
//
//    // Cell mode — in _renderBodyCell for editable columns:
//    const isEditing = this._edit.isCellEditing(key, field);
//
//    // Row mode — in _renderBodyCell and row-editor column:
//    const isEditing = this._edit.isRowEditing(key);
//
// ─────────────────────────────────────────────────────────────────────────────

import type { ReactiveController, ReactiveControllerHost } from 'lit';

export type DataTableEditMode = 'cell' | 'row';

export class EditController implements ReactiveController {
  private readonly _host: ReactiveControllerHost & EventTarget;
  private _getKey: (row: Record<string, unknown>) => unknown;

  // ── Cell edit state ────────────────────────────────────────────────────────
  private _activeCellKey: unknown = undefined;
  private _activeCellField: string | null = null;
  /** Shallow clone of the cell's field value before editing began (for cancel). */
  private _originalCellValue: unknown = undefined;

  // ── Row edit state ─────────────────────────────────────────────────────────
  /** Keys of rows currently in edit mode. Value is any number > 0 (matching PrimeNG convention). */
  private _editingRowKeys = new Map<unknown, number>();
  /** Shallow clone of each row's own props before editing began (for cancel). */
  private _originalRows = new Map<unknown, Record<string, unknown>>();

  /** Mirrors the host's editMode property. */
  editMode?: DataTableEditMode;

  constructor(
    host: ReactiveControllerHost & EventTarget,
    getKey: (row: Record<string, unknown>) => unknown,
  ) {
    this._host   = host;
    this._getKey = getKey;
    host.addController(this);
  }

  hostConnected()    {}
  hostDisconnected() {}

  // ── Cell edit public API ───────────────────────────────────────────────────

  /** True when this specific cell (row + field combination) is in edit mode. */
  isCellEditing(rowKey: unknown, field: string): boolean {
    return this._activeCellKey === rowKey && this._activeCellField === field;
  }

  /** True when any cell is currently in edit mode. */
  isAnyCellEditing(): boolean {
    return this._activeCellKey !== undefined;
  }

  /** Enter edit mode for a single cell. Fires `uwc-cell-edit-init`. */
  initCellEdit(row: Record<string, unknown>, field: string, index: number): void {
    const key = this._getKey(row);
    if (this.isCellEditing(key, field)) return;

    // Capture original value (shallow — primitives only; deep clone is author's concern).
    this._originalCellValue = row[field];
    this._activeCellKey     = key;
    this._activeCellField   = field;

    this._host.dispatchEvent(new CustomEvent('uwc-cell-edit-init', {
      bubbles: true, composed: true,
      detail: { data: row, field, index },
    }));
    this._host.requestUpdate();
  }

  /**
   * Confirm the current cell edit. Fires `uwc-cell-edit-complete`.
   * The row object is already mutated in place (the input updated `rowData[field]`);
   * the author is responsible for persisting the change.
   */
  completeCellEdit(row: Record<string, unknown>, field: string, index: number): void {
    const key = this._getKey(row);
    if (!this.isCellEditing(key, field)) return;

    this._activeCellKey      = undefined;
    this._activeCellField    = null;
    this._originalCellValue  = undefined;

    this._host.dispatchEvent(new CustomEvent('uwc-cell-edit-complete', {
      bubbles: true, composed: true,
      detail: { data: row, field, index },
    }));
    this._host.requestUpdate();
  }

  /**
   * Cancel the current cell edit. Restores the original field value.
   * Fires `uwc-cell-edit-cancel`.
   */
  cancelCellEdit(row: Record<string, unknown>, field: string, index: number): void {
    const key = this._getKey(row);
    if (!this.isCellEditing(key, field)) return;

    // Restore in-place so the Lit reactive property value array stays consistent.
    row[field] = this._originalCellValue;

    this._activeCellKey      = undefined;
    this._activeCellField    = null;
    this._originalCellValue  = undefined;

    this._host.dispatchEvent(new CustomEvent('uwc-cell-edit-cancel', {
      bubbles: true, composed: true,
      detail: { data: row, field, index },
    }));
    this._host.requestUpdate();
  }

  // ── Row edit public API ────────────────────────────────────────────────────

  /** True when the row identified by `rowKey` is in edit mode. */
  isRowEditing(rowKey: unknown): boolean {
    return this._editingRowKeys.has(rowKey);
  }

  /**
   * Enter edit mode for an entire row. Fires `uwc-row-edit-init`.
   * Stores a shallow clone for potential cancel.
   */
  initRowEdit(row: Record<string, unknown>, index: number): void {
    const key = this._getKey(row);
    if (this._editingRowKeys.has(key)) return;

    // Shallow clone covers primitive fields; nested objects are author's concern.
    this._originalRows.set(key, { ...row });
    this._editingRowKeys.set(key, 1);

    this._host.dispatchEvent(new CustomEvent('uwc-row-edit-init', {
      bubbles: true, composed: true,
      detail: { data: row, index },
    }));
    this._host.requestUpdate();
  }

  /**
   * Save the row edit. Exits edit mode; fires `uwc-row-edit-save`.
   * The row is already mutated in place; the author persists the change.
   */
  saveRowEdit(row: Record<string, unknown>, index: number): void {
    const key = this._getKey(row);
    if (!this._editingRowKeys.has(key)) return;

    this._editingRowKeys.delete(key);
    this._originalRows.delete(key);

    this._host.dispatchEvent(new CustomEvent('uwc-row-edit-save', {
      bubbles: true, composed: true,
      detail: { data: row, index },
    }));
    this._host.requestUpdate();
  }

  /**
   * Cancel the row edit. Restores all original field values in-place.
   * Fires `uwc-row-edit-cancel` with both `data` (current, now restored) and
   * `originalData` (the saved clone) for author convenience.
   */
  cancelRowEdit(row: Record<string, unknown>, index: number): void {
    const key      = this._getKey(row);
    const original = this._originalRows.get(key);

    if (!this._editingRowKeys.has(key)) return;

    // Restore original values in-place so the existing array reference is kept.
    if (original) {
      Object.assign(row, original);
    }

    this._editingRowKeys.delete(key);
    this._originalRows.delete(key);

    this._host.dispatchEvent(new CustomEvent('uwc-row-edit-cancel', {
      bubbles: true, composed: true,
      detail: { data: row, originalData: original, index },
    }));
    this._host.requestUpdate();
  }

  /**
   * Sync internal editing-row state from the host's `editingRowKeys` property.
   * Rows already tracked are left unchanged (preserving their original clones);
   * rows absent from the new map are removed.
   */
  syncRowKeys(keys?: Map<unknown, number>): void {
    if (!keys) {
      this._editingRowKeys = new Map();
      return;
    }
    const next = new Map<unknown, number>();
    for (const [k, v] of keys) {
      next.set(k, v);
    }
    this._editingRowKeys = next;
  }
}
